import { Food } from './food.model';
import { Product } from './product.model';
import { Energy } from './energy.model';
import { dateFromTimestamp, FORMAT_TIMESTAMP } from '../utils/dateFormatter';
import {CARBO, FIBER} from "../services/NutrientDetailsProvider";

export class Plan {

  constructor(id, name, prescribedEnergy, time, comment, foods = [], products = [], date = null) {
    this._id = id;
    this._name = name;
    this._comment = comment;
    this._prescribedEnergy = prescribedEnergy;
    this._foods = foods;
    this._products = products;

    this.setDateTime(date,time);
  }

  get id() {
    return this._id;
  }

  set id(id) {
    this._id = id;
  }

  get name() {
    return this._name;
  }

  set name(name) {
    this._name = name;
  }

  get time() {
    return this._time;
  }

  set time(time) {
    this._time = time;

    const date = dateFromTimestamp(this._date);
    const [hour, minute] = time.split(':');

    date.hour(hour);
    date.minute(minute);

    this._date = Number(date.format(FORMAT_TIMESTAMP));

    this._foods.map(f => f.date = this._date);
  }

  get comment() {
    return this._comment;
  }

  set comment(comment) {
    this._comment = comment;
  }


  set foods(foods) {
    foods.map(f => this.addFood(f));
  }

  get foods() {
    return this._foods;
  }

  set products(products) {
    this._products = products;
  }

  get products() {
    return this._products;
  }

  get totalEnergy() {
    const sum = (prev, total) => prev + total;
    const total = this._foods.map((m) => m.calculateEnergy().amount).reduce(sum, 0) +
      this._products.map((p) => p.energyInPortion.amount).reduce(sum, 0);

    return new Energy(total);
  }

  get prescribedEnergy() {
    return this._prescribedEnergy;
  }

  set prescribedEnergy(prescribedEnergy) {
    this._prescribedEnergy = prescribedEnergy;
  }

  get date() {
    return Number(this._date);
  }

  set date(date) {
    this._date = date;
  }

  caloriesFulfillment() {
    return Math.round((this.totalEnergy.amount / this._prescribedEnergy.amount) * 100) / 100;
  }

  getFood(id) {
    return this._foods.find(p => p.id === id);
  }

  addFood(food) {
    if (!(food instanceof Food)) {
      throw new Error('Unsupported type');
    }

    const foodExists = this.findFood(food.hash, 'hash');
    if (foodExists && food.isMultiPortionFood() === false) {
      foodExists.portion.add(food.portion);
      return;
    }

    food.date = this.date;
    this._foods.push(food);
  }

  findFood(id, key = 'externalId') {
    return this._foods.find((p) => p[key] === id);
  }

  removeFood(id) {
    this._foods = this._foods.filter(p => p.id !== id);
  }

  addProduct(newProduct) {
    if (!(newProduct instanceof Product)) {
      throw new Error('Unsupported type');
    }

    const product = this.findProductByIdPortion(newProduct.externalId, newProduct.portion);
    if (product) {
      product.portion.add(newProduct.portion);
      return;
    }

    this._products.push(newProduct);
  }

  findProduct(id, key = 'externalId') {
    return this._products.find((p) => p[key] === id);
  }

  findProductsBy(value, key) {
    return this._products.filter((product) => product[key] === value);
  }

  findProductByIdPortion(externalId, portion) {
    return this._products.find(p => p.externalId === externalId && p.portion.isEqual(portion));
  }

  removeProduct(id) {
    this._products = this._products.filter(p => p.id !== id);
  }

  replaceProduct(id, productReplacement) {
    const productByPortion = this.findProductByIdPortion(productReplacement.externalId, productReplacement.portion);
    if (productByPortion && productByPortion.id !== id) {
      this.removeProduct(id);
      this.addProduct(productReplacement);
    } else if(typeof productByPortion === 'undefined'){
      const productIndex = this._products.findIndex((product) => product.id === id);
      if (productIndex !== -1) {
        const products = [...this._products];
        products[productIndex] = productReplacement;
        this._products = products;
      }
    }
  }

  nutrients() {
    const total = {};

    const foodsNutrients = this.foods.map((f) => f.nutrients(true));
    const productsNutrients = this.products.map((p) => p.getNutrientsInPortion());

    foodsNutrients.forEach((foodNutrients) => {
      for (const externalId in foodNutrients) {
        if (!total[externalId]) {
          total[externalId] = 0;
        }
        total[externalId] += foodNutrients[externalId];
      }
    });

    productsNutrients.forEach((productNutrients) => {
      for (const externalId in productNutrients) {
        if (!total[externalId]) {
          total[externalId] = 0;
        }

        total[externalId] += productNutrients[externalId];
      }
    });

    return total;
  }

  sumNutrientById(id) {
    const productsNutrients = this._products.map((p) => p.sumNutrientById(id)).reduce((p, t) => p + t, 0);
    const foodsNutrients = this._foods.map((f) => f.sumNutrientById(id, true)).reduce((p, t) => p + t, 0);

    return productsNutrients + foodsNutrients;
  }

  calculateGlicemicLoad() {
    const productsLoad = this._products.map(p => p.calculateGlicemicLoad()).reduce((p, t) => p + t, 0);
    const foodsLoad = this._foods.map(f => f.calculateGlicemicLoad()).reduce((p, t) => p + t, 0);

    return productsLoad + foodsLoad;
  }

  calculateGlicemicIndex() {
    const totalCarbohydrates = this.sumNutrientById(CARBO);
    const totalFiber = this.sumNutrientById(FIBER);
    const totalDigestibleCarbohydrates = totalCarbohydrates - totalFiber;

    if (totalDigestibleCarbohydrates === 0) {
      return 0;
    }

    let totalGlicemicIndex = 0;

    this._foods.forEach((food) => {
      food.products.forEach((foodProduct) => {
        const productGlicemicIndex = foodProduct.getGlicemicIndex();
        let productCarbohydrates = foodProduct.getCarbohydrates();
        let productFiber = foodProduct.getFiber();

        productCarbohydrates = productCarbohydrates / food.portions * food.portion.amount;
        productFiber = productFiber / food.portions * food.portion.amount;

        const percentage = (productCarbohydrates - productFiber) / (totalDigestibleCarbohydrates);

        totalGlicemicIndex += percentage * productGlicemicIndex;
      });
    });

    this._products.forEach((p) => {
      const productGlicemicIndex = p.getGlicemicIndex();
      let productCarbohydrates = p.getCarbohydrates();
      let productFiber = p.getFiber();

      const percentage = (productCarbohydrates - productFiber) / (totalDigestibleCarbohydrates);

      totalGlicemicIndex += percentage * productGlicemicIndex;
    });

    return totalGlicemicIndex;
  }

  hasComment() {
    return this._comment.length > 0;
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      prescribedEnergy: this.prescribedEnergy,
      time: this.time,
      comment: this.comment,
      foods: this.foods,
      products: this.products,
    };
  }

  setDateTime(date, time) {
    this._date = date;
    this.time = time;
  }
}
